<template>
  <div class="row justify-content-left mt-5 dp02 p-3">
    <div class="col-lg-12">
      <h2>
        {{ $t('EditTemplateCategory.Header') }}
      </h2>
    </div>
    <div class="col-lg-12">
      <div class="row my-3">
        <div class="col-lg-3">
          <label for="gender">{{ $t('common.Name') }}:</label>
        </div>
        <div class="col-lg-9">
          <b-form-input
            class="myInput"
            v-model="name" />
        </div>
      </div>
      <div class="row my-3">
        <div class="col-lg-3">
          <label for="gender">{{ $t('common.NameEn') }}:</label>
        </div>
        <div class="col-lg-9">
          <b-form-input
            class="myInput"
            v-model="nameEn" />
        </div>
      </div>
    </div>
    <div class="col-lg-12 mt-3 h-100"
         style="min-height:20vh">
      <h2>{{ $t('common.Description') }}</h2>
      <ckeditor v-model="description"
                :editor="editor"
                @ready="onReady"
                :config="editorConfig" />
    </div>
    <div class="col-lg-12 mt-3 h-100"
         style="min-height:20vh">
      <h2>{{ $t('common.DescriptionEn') }}</h2>
      <ckeditor v-model="descriptionEn"
                :editor="editor"
                @ready="onReady"
                :config="editorConfig" />
    </div>
    <div class="col-lg-12 mt-3 h-100"
         style="min-height:20vh">
      <h2>{{ $t('common.References') }}</h2>
      <ckeditor v-model="references"
                :editor="editor"
                @ready="onReady"
                :config="editorConfig" />
    </div>
    <div class="col-lg-12 mt-5">
      <b-button
        type="submit"
        class="myButton align-middle"
        @click="editTemplate()">
        {{ $t('EditTemplateCategory.EditTemplateCategory') }}
      </b-button>
      <b-spinner
        v-if="requestSent"
        variant="primary"
        class="align-middle ml-3"
        label="Spinning" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import DecoupledEditor from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor';
import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials';
import UploadAdapterPlugin from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import AutoformatPlugin from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import HeadingPlugin from '@ckeditor/ckeditor5-heading/src/heading';
import FontFamilyPlugin from '@ckeditor/ckeditor5-font/src/fontfamily';
import FontSizePlugin from '@ckeditor/ckeditor5-font/src/fontsize';
import FontColorPlugin from '@ckeditor/ckeditor5-font/src/fontcolor';
import FontBackgroundColorPlugin from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';
import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold';
import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic';
import UnderlinePlugin from '@ckeditor/ckeditor5-basic-styles/src/underline';
import StrikethroughPlugin from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import AlignmentPlugin from '@ckeditor/ckeditor5-alignment/src/alignment';
import ListPlugin from '@ckeditor/ckeditor5-list/src/list';
import IndentPlugin from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlockPlugin from '@ckeditor/ckeditor5-indent/src/indentblock';
import LinkPlugin from '@ckeditor/ckeditor5-link/src/link';
import BlockQuotePlugin from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import EasyImagePlugin from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import ImagePlugin from '@ckeditor/ckeditor5-image/src/image';
import ImageCaptionPlugin from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStylePlugin from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbarPlugin from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUploadPlugin from '@ckeditor/ckeditor5-image/src/imageupload';
import TablePlugin from '@ckeditor/ckeditor5-table/src/table';
import TableToolbarPlugin from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import MediaEmbedPlugin from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import HorizontalLinePlugin from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import Adapter from '@utils/base64uploadadapter';

export default {
  name: 'EditTemplateCategory',
  data() {
    return {
      editor: DecoupledEditor,
      editorConfig: {
        plugins: [
          EssentialsPlugin,
          AutoformatPlugin,
          HeadingPlugin,
          FontFamilyPlugin,
          FontSizePlugin,
          FontColorPlugin,
          FontBackgroundColorPlugin,
          BoldPlugin,
          ItalicPlugin,
          UnderlinePlugin,
          StrikethroughPlugin,
          AlignmentPlugin,
          ListPlugin,
          IndentPlugin,
          IndentBlockPlugin,
          LinkPlugin,
          BlockQuotePlugin,
          ImagePlugin,
          ImageCaptionPlugin,
          ImageStylePlugin,
          ImageToolbarPlugin,
          EasyImagePlugin,
          ImageUploadPlugin,
          TablePlugin,
          TableToolbarPlugin,
          MediaEmbedPlugin,
          ParagraphPlugin,
          UploadAdapterPlugin,
          HorizontalLinePlugin
        ],
        toolbar: [
          'heading',
          '|',
          'fontFamily',
          'fontSize',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'alignment',
          '|',
          'numberedList',
          'bulletedList',
          '|',
          'outdent',
          'indent',
          '|',
          'link',
          'blockQuote',
          'imageUpload',
          'insertTable',
          'mediaEmbed',
          '|',
          'undo',
          'redo',
          '|',
          'horizontalLine'
        ],
        fontFamily: {
          supportAllValues: true
        },
        fontSize: {
          options: [
            'tiny',
            'small',
            'default',
            'big',
            'huge'
          ]
        },
        image: {
          toolbar: [
            'imageStyle:full',
            'imageStyle:side',
            '|',
            'imageTextAlternative'
          ]
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        }
      },
      requestSent: false
    };
  },
  created() {
    this.resetContext();
    this.setTemplateCategoryId(this.id);
    this.fetchTemplateCategory();
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    description: {
      get() {
        return this.getDescription();
      },
      set(val) {
        this.setDescription(val);
      }
    },
    descriptionEn: {
      get() {
        return this.getDescriptionEn();
      },
      set(val) {
        this.setDescriptionEn(val);
      }
    },
    name: {
      get() {
        return this.getName();
      },
      set(val) {
        this.setName(val);
      }
    },
    nameEn: {
      get() {
        return this.getNameEn();
      },
      set(val) {
        this.setNameEn(val);
      }
    },
    references: {
      get() {
        return this.$store.state.editTemplateCategory.references;
      },
      set(val) {
        this.setReferences(val);
      }
    }
  },
  methods: {
    ...mapActions({
      editTemplateCategory: 'editTemplateCategory/editTemplateCategory',
      fetchTemplateCategory: 'editTemplateCategory/fetchTemplateCategory',
      resetContext: 'editTemplateCategory/resetContext'
    }),
    ...mapMutations({
      setDescription: 'editTemplateCategory/setDescription',
      setDescriptionEn: 'editTemplateCategory/setDescriptionEn',
      setReferences: 'editTemplateCategory/setReferences',
      setTemplateCategoryId: 'editTemplateCategory/setTemplateCategoryId',
      setName: 'editTemplateCategory/setName',
      setNameEn: 'editTemplateCategory/setNameEn'
    }),
    ...mapGetters({
      getDescription: 'editTemplateCategory/getDescription',
      getDescriptionEn: 'editTemplateCategory/getDescriptionEn',
      getName: 'editTemplateCategory/getName',
      getNameEn: 'editTemplateCategory/getNameEn'
    }),
    onReady(editor) {
      // Insert the toolbar before the editable area.
      editor.ui
        .getEditableElement()
        .parentElement.insertBefore(
          editor.ui.view.toolbar.element,
          editor.ui.getEditableElement()
        );
      editor.plugins.get('FileRepository').createUploadAdapter = (loader) => new Adapter(loader);
    },
    editTemplate() {
      this.requestSent = true;
      this.editTemplateCategory()
        .then(() => {
          this.requestSent = false;
          this.resetContext();
          this.$root.$bvToast.toast(this.$t('EditTemplateCategory.SuccessToast.Body'), {
            title: this.$t('EditTemplateCategory.SuccessToast.Title'),
            variant: 'success',
            toaster: 'b-toaster-top-right'
          });
          this.$router.replace({ name: 'admin.templates.templatecategories.view' });
        })
        .catch(() => {
          this.requestSent = false;
          this.$root.$bvToast.toast(this.$t('EditTemplateCategory.FailureToast.Body'), {
            title: this.$t('EditTemplateCategory.FailureToast.Title'),
            variant: 'danger',
            toaster: 'b-toaster-top-right'
          });
        });
    }
  }
};
</script>

<style lang="scss" scoped>
</style>
